import { useEffect, useRef, useState } from 'react';
import { connect, Dispatch } from "react-redux";
import { IApplicationState, AppActionCreators, RecipientActionCreators, IHighDeclineListResponse, IDonorsPerMonthByAboResponse, TargetActionCreators } from "@nkr/store/index";
import { IDonorsPerMonthByAbo, IEwtSummary, IMortalityCalculations, IFwtSummary, ITargetItem, IUser, IRecipient } from '@nkr/models/index';
import Tabs from '@nkr/views/swap-management/tabs-new';
import Vouchers from './vouchers';
import PairedPatients from './paired-patients';
import EwtDetails from '../target-new/modals/ewt-details';
import MortalityBreakdown from '@nkr/components/mortality-breakdown-modal';
import FwlDetailsModal from '../target-new/modals/fwt-details-modal';
import { Button } from 'react-bootstrap';
import { ColumnApi, exportMultipleSheetsAsExcel, GridApi } from 'ag-grid-enterprise';
import { GridReadyEvent } from 'ag-grid-community';

interface IOwnProps {
}

interface IStateProps {
    user: IUser,
}

interface IDisptachProps {
    showLoading: (message: string) => Promise<void>,
    hideLoading: () => void,
    getHighDeclines: () => Promise<IHighDeclineListResponse>,
    getDonorsPerMonthByAbo: () => Promise<IDonorsPerMonthByAboResponse>
}

type TProps = IStateProps & IDisptachProps & IOwnProps;

const HighDeclines = (props: TProps) => {
    const [hardToMatchList, setHardToMatchList] = useState<ITargetItem[]>([]);
    const [voucherList, setVoucherList] = useState<ITargetItem[]>([]);
    const [unmatchedPairList, setUnmatchedPairList] = useState<ITargetItem[]>([]);
    const [targetChainEndCutoff, setTargetChainEndCutoff] = useState(0);
    const [unmatchedPairCutoff, setUnmatchedPairCutoff] = useState(0);
    const [gridApis, setGridApis] = useState<any[]>([]);
    const [columnApi, setColumnApi] = useState<ColumnApi>(null);
    const recipientRef = useRef(null);

    const [donorsPerMonthByAbo, setDonorsPerMonthAbo] = useState<IDonorsPerMonthByAbo>(null);

    const [ewt, setEwt] = useState<{
        show: boolean,
        ewtSummary: IEwtSummary,
        initialCount: { [key: string]: number, O: number, A: number, B: number, AB: number }
    }>({
        show: false,
        ewtSummary: null,
        initialCount: null
    });

    const [fwt, setFwt] = useState<{
        show: boolean,
        fwtSummary: IFwtSummary
    }>({
        show: false,
        fwtSummary: null
    });

    const [mortalityBreakdown, setMortalityBreakdown] = useState<{ show: boolean, recipientAlias: string, data: IMortalityCalculations }>({ show: false, recipientAlias: '', data: null });

    const onGridReady = (e: GridReadyEvent) => {
        setGridApis(prev => [...prev, e.api]);
    };

    useEffect(() => {
        getData();
    }, []);

    const getData = () => {
        props.showLoading('Retrieving high decline recipients, one moment please...').then(() => {
            Promise.all([
                props.getHighDeclines(),
                props.getDonorsPerMonthByAbo()
            ])
                .then(([responseHighDeclines, donorsPerMonthByAboResponse]) => {
                    if (responseHighDeclines.data.success) {
                        let { hardToMatchList, voucherList, unmatchedPairList, targetChainEndCutoff, unmatchedPairCutoff } = responseHighDeclines.data.results;
                        let donorsPerMonthByAbo = donorsPerMonthByAboResponse.data.results;

                        setHardToMatchList(hardToMatchList);
                        setVoucherList(voucherList);
                        setUnmatchedPairList(unmatchedPairList);
                        setTargetChainEndCutoff(targetChainEndCutoff);
                        setUnmatchedPairCutoff(unmatchedPairCutoff);
                        setDonorsPerMonthAbo(donorsPerMonthByAbo);
                    }
                })
                .finally(() => {
                    props.hideLoading();
                });
        });
    };

    const handleShowEwt = (summary: IEwtSummary, initialCount: any) => {
        setEwt({
            show: true,
            ewtSummary: summary,
            initialCount
        });
    };

    const handleEwtClose = () => {
        setEwt({ ...ewt, show: false });
    };

    const handleShowFwt = (recipient: ITargetItem) => {
        let isHardToMatch = recipient?.hardToMatch;
        let isVoucherHolder = recipient?.isVoucherHolder;
        let isPaired = recipient?.isPairedRecipient;


        let fwtSummary: IFwtSummary = {
            recipientId: recipient.recipientId,
            recipientAlias: recipient.recipientAlias,
            hardToMatch: isHardToMatch,
            isVoucherHolder,
            isPairedRecipient: isPaired,
            patientCpra: recipient?.cPRA4 ?? 0,
            patientHistoricalDeclineRatio: recipient?.declineRatioHistoric ?? 0,
            donorsNeeded: recipient?.donorsNeeded ?? 0,
            aboCombo: recipient?.aboCombo ?? '',
            donorsCompatibleABO: recipient?.donorCompatibleAbo.toString() ?? '',
            aboAndAgeCompatibleDonors: recipient?.aboAndAgeCompatibleDonors.toString() ?? '',
            recipientBloodType: recipient?.recipientBloodType ?? '',
            categories: recipient?.categories ?? [],
            fwt: recipient?.forecastedWaitTime.toString() ?? '',
            ewt: recipient?.wait.toString() ?? '',
            awt: recipient?.aWait.toString() ?? '',           
            age: recipient?.rAge ?? 0            
        };

        setFwt({
            show: true,
            fwtSummary
        });
    };

    const handleFwtClose = () => {
        setFwt({ ...fwt, show: false });
    };

    const handleShowMortalityBreakdown = (type: number, recipient: ITargetItem) => {
        setMortalityBreakdown({
            show: true,
            recipientAlias: recipient.recipientAlias,
            data: recipient.mortalityCalculations
        })
    }

    const handleMortalityBreakdownClose = () => {
        setMortalityBreakdown({ show: false, recipientAlias: '', data: null });
    }

    const handleOpenEWTPage = (recipientId: number) => {
        let combinedList = [
            ...hardToMatchList.map(x => ({ ...x, bucket: 'HTM' })),
            ...voucherList.map(x => ({ ...x, bucket: 'VL' })),
            ...unmatchedPairList.map(x => ({ ...x, bucket: 'UMPairedPatient' })),
        ];

        let recipient = combinedList.find(x => x.recipientId == recipientId);

        if (!recipient) return;

        recipientRef.current = recipient;

        // Step 1: hide fwt
        setFwt(prev => ({ ...prev, show: false }));
    };

    useEffect(() => {
        if (!fwt.show && recipientRef.current) {
            let recipient = recipientRef.current;

            setEwt({
                show: true,
                ewtSummary: recipient.ewtSummary,
                initialCount: recipient.initialCount,
            });

            recipientRef.current = null;
        }
    }, [fwt.show]);


    const getTimeDateString = () => {
        let now = new Date();

        let year = now.getFullYear();
        let month = String(now.getMonth() + 1).padStart(2, '0'); // Months are zero-based
        let day = String(now.getDate()).padStart(2, '0');
        let hours = String(now.getHours()).padStart(2, '0');
        let minutes = String(now.getMinutes()).padStart(2, '0');
        let seconds = String(now.getSeconds()).padStart(2, '0');
        return `${year}-${month}-${day}_${hours}-${minutes}-${seconds}`;
    }


    const handleExport = () => {
        const dateStamp = getTimeDateString();
        const fileName = `donor-declines-data-${dateStamp}.xlsx`;
        const sheetNames = ['Vouchers', 'Paired Patients'];
        const spreadsheets = [];

        const transformValue = (field, value) => {
            if (typeof value != 'number') return value;

            switch (field) {
                case 'declineRatioCurrent':
                case 'highestDeclineRatio':
                case 'cprScore':
                    return Math.round(value * 100);
                case 'cPRA4':
                case 'declineRatioHistoric':
                    return Math.round(value);
                default:
                    return value;
            }
        };

        for (let i = 0; i < gridApis.length; i++) {
            const api = gridApis[i];
            if (api) {
                const sheetData = api.getSheetDataForExcel({
                    sheetName: sheetNames[i],
                    processCellCallback: ({ column, value }) =>
                        transformValue(column.getColDef().field, value)
                });
                spreadsheets.push(sheetData);
            }
        }

        exportMultipleSheetsAsExcel({ data: spreadsheets, fileName });
    };



    return (
        <>
            <Tabs
                tabKey='HIGH-DECLINES'
                subTabKey=''
                counts={null}
                user={props.user} />

            <div style={{ display: 'inline-flex' }}>
                <div style={{ marginTop: 10, marginBottom: 20, fontSize: 28, fontWeight: 500 }}>High Risk Patients</div>

                {/* <div style={{ marginTop: 20, marginLeft: '20px' }}>
                    1-Yr MR is the 1-year mortality risk, which is the inverse of the 1-year survival % (e.g. 8% risk of death vs. 92% chance of survival)
                </div> */}
            </div>

            <div style={{ position: 'absolute', top: 0, right: 20 }}>
                <Button bsSize='xs' bsStyle='success' onClick={handleExport}>Export CSV</Button>
            </div>

            <Vouchers
                list={voucherList}
                cutoff={targetChainEndCutoff}
                onShowEwt={handleShowEwt}
                onShowFwt={handleShowFwt}
                onShowMortalityBreakdown={handleShowMortalityBreakdown}
                onGridReady={onGridReady}
            />

            <PairedPatients
                list={unmatchedPairList}
                cutoff={unmatchedPairCutoff}
                onShowEwt={handleShowEwt}
                onShowFwt={handleShowFwt}
                onShowMortalityBreakdown={handleShowMortalityBreakdown}
                onGridReady={onGridReady}
            />

            <EwtDetails
                show={ewt.show}
                summaryData={ewt.ewtSummary}
                initialCount={ewt.initialCount}
                donorsPerMonthByAbo={donorsPerMonthByAbo}
                onClose={handleEwtClose}
            />

            <FwlDetailsModal
                show={fwt.show}
                summaryData={fwt.fwtSummary}
                showLink={true}
                onOpenEWTModal={handleOpenEWTPage}
                onClose={handleFwtClose}
            />

            {/* put mortality calc here */}
            <MortalityBreakdown
                show={mortalityBreakdown.show}
                recipientAlias={mortalityBreakdown.recipientAlias}
                data={mortalityBreakdown.data}
                onClose={handleMortalityBreakdownClose}
            />
        </>
    );
}

const mapStateToProps = (state: IApplicationState, ownProps: IOwnProps): IStateProps => {
    return {
        user: state.app.user,
    }
}

const mapDisptachToProps = (dispatch: Dispatch<any>): IDisptachProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getHighDeclines: () => {
            return dispatch(RecipientActionCreators.getHighDeclines());
        },
        getDonorsPerMonthByAbo: () => {
            return dispatch(TargetActionCreators.getDonorsPerMonthByAbo());
        },
    };
}

export default connect<IStateProps, IDisptachProps, IOwnProps>(
    mapStateToProps,
    mapDisptachToProps)(HighDeclines);

